import { concat, hash } from '@ember/helper';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { focusTrap } from 'ember-focus-trap';
import { ref } from 'ember-ref-bucket';
import createRef from 'ember-ref-bucket/modifiers/create-ref';
import and from 'ember-truth-helpers/helpers/and';
import eq from 'ember-truth-helpers/helpers/eq';
import not from 'ember-truth-helpers/helpers/not';
import or from 'ember-truth-helpers/helpers/or';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import classNames from 'volta/helpers/class-names';
import BvBtn from 'volta/components/bv-btn';
import BvInline from 'volta/components/bv-inline';
import BvPopover from 'volta/components/bv-popover';
import BvTooltip from 'volta/components/bv-tooltip';

import type { ContentValue } from '@glint/template';
import type { Dropdown } from 'ember-basic-dropdown/components/basic-dropdown';
import type { CalculatePositionResult } from 'ember-basic-dropdown/utils/calculate-position';
import type { TError } from 'volta/utils/error-utils';
import type { TText } from 'volta/utils/text-utils';
import type { BvFocusUnitModifier } from 'volta/components/bv-focus-zone';
import type { BvPopoverApi, BvPopoverSignature } from 'volta/components/bv-popover';
import type { TTooltipSide } from 'volta/components/bv-tooltip';

export interface BvInlineEditApi {
  isEditing: boolean;
  isLoading: boolean;
  popover: BvPopoverApi;
  toggle: Dropdown['actions']['toggle'];
  close: Dropdown['actions']['close'];
  confirm: (e: Event) => Promise<void> | void;
}

type PopoverArgs = Pick<
  BvPopoverSignature['Args'],
  | 'disabled'
  | 'horizontalPosition'
  | 'isMenu'
  | 'matchTriggerWidth'
  | 'onOpen'
  | 'preventScroll'
  | 'renderInPlace'
  | 'tabindex'
  | 'verticalPosition'
  | 'size'
>;

export type BvInlineEditArgs = PopoverArgs & {
  tooltipSide?: TTooltipSide;
  tooltipText?: TText;
  flushBody?: boolean;
  withDisplay?: boolean;
  title?: ContentValue;
  hideDefaultActions?: boolean;
  fullWidth?: boolean;
  stopPropagation?: boolean;
  focusZoneUnit?: BvFocusUnitModifier;
  enableFocusTrap?: boolean;
  testName?: string;
  displayClassNames?: string;
  onSave?: (e: Event) => void | Promise<void>;
  onCancel?: () => void;
};

export type BvInlineEditCommonArgs = Pick<
  BvInlineEditArgs,
  | 'hideDefaultActions'
  | 'matchTriggerWidth'
  | 'disabled'
  | 'tooltipText'
  | 'testName'
  | 'displayClassNames'
>;

interface BvInlineEditSignature {
  Args: BvInlineEditArgs;
  Blocks: {
    actions: [BvInlineEditApi];
    default: [BvInlineEditApi];
    editing: [BvInlineEditApi];
  };
  Element: HTMLElement;
}

export default class BvInlineEditComponent extends Component<BvInlineEditSignature> {
  <template>
    <BvPopover
      @horizontalPosition={{or @horizontalPosition 'center'}}
      @verticalPosition={{or @verticalPosition 'auto'}}
      @matchTriggerWidth={{@matchTriggerWidth}}
      @preventScroll={{@preventScroll}}
      @disabled={{@disabled}}
      @applyCustomPosition={{this.setPositionCorrectly}}
      @onClose={{this.handlePopoverClose}}
      @onOpen={{@onOpen}}
      @onInit={{this.handlePopoverInit}}
      @isMenu={{@isMenu}}
      @tabindex={{@tabindex}}
      @onDropdownChange={{this.handlePopoverInit}}
      @popoverClass='bv-popover__inline-edit'
      @size={{@size}}
      as |popover|
    >
      {{#let
        (hash
          isEditing=popover.isOpen
          isLoading=this.isLoading
          popover=popover
          toggle=popover.toggle
          close=popover.close
          confirm=this.handleConfirmation
        )
        as |publicApi|
      }}
        <popover.Trigger
          class={{classNames
            'bv-inline-edit'
            (unless popover.isOpen 'bv-inline-edit--show')
            (if @fullWidth 'bv-inline-edit--full-width')
            (if @disabled 'bv-inline-edit--disabled')
          }}
          @stopPropagation={{@stopPropagation}}
          data-test-bv-inline-edit-trigger={{@testName}}
          {{! @glint-expect-error: EmberBasicDropdown.TriggerComponent has Element as Element type instead of HTMLElement }}
          ...attributes
          {{! @glint-expect-error: EmberBasicDropdown.TriggerComponent has Element as Element type instead of HTMLElement }}
          {{@focusZoneUnit focusApi=popover.api}}
        >
          <div
            class={{classNames 'bv-inline-edit__display' @displayClassNames}}
            {{createRef 'displayElement' bucket=this}}
          >
            {{yield publicApi}}
            {{#if @tooltipText}}
              <BvTooltip @text={{@tooltipText}} @side={{@tooltipSide}} />
            {{/if}}
          </div>
        </popover.Trigger>
        <popover.Content
          {{focusTrap
            isActive=(and (eq @enableFocusTrap true) popover.isOpen)
            focusTrapOptions=popover.focusTrapOptions
          }}
        >
          {{#if (has-block 'header')}}
            {{yield publicApi}}
          {{else if @title}}
            <popover.Header @showCloseButton={{not @hideDefaultActions}} @title={{@title}} />
          {{/if}}
          <popover.Body @flush={{@flushBody}}>
            {{#if @withDisplay}}
              <div class={{if (has-block 'editing') 'u-mb-2'}}>
                {{yield publicApi}}
              </div>
            {{/if}}
            {{#if (has-block 'editing')}}
              <div
                class='bv-inline-edit__editing'
                role='button'
                {{@focusZoneUnit focusId=popover.uniqueId}}
              >
                {{yield publicApi to='editing'}}
              </div>
            {{/if}}
          </popover.Body>
          {{#if (or (has-block 'actions') (not @hideDefaultActions))}}
            <popover.Footer>
              <BvInline
                @blockAlign='center'
                @align={{if (has-block 'actions') 'space-between' 'end'}}
                class='u-full-width'
              >
                {{#if (has-block 'actions')}}
                  {{yield publicApi to='actions'}}
                {{/if}}
                {{#unless @hideDefaultActions}}
                  <BvBtn
                    @onClick={{this.handleConfirmation}}
                    @iconLeft='checkmark-outline'
                    @color='primary'
                    @size='sm'
                    @testName={{concat 'inline-edit-confirm-' @testName}}
                    @disabled={{@disabled}}
                  />
                {{/unless}}
              </BvInline>
            </popover.Footer>
          {{/if}}
        </popover.Content>
      {{/let}}
    </BvPopover>
  </template>
  @tracked isLoading: boolean = false;
  @tracked popover: Dropdown | null = null;

  @ref('displayElement')
  displayElement: HTMLInputElement | null = null;

  @action
  handlePopoverInit(api: Dropdown | null) {
    this.popover = api;
  }

  @action
  handlePopoverClose(_api: Dropdown, event?: Event) {
    if ((event?.target as HTMLElement) === document.body) {
      return false;
    }

    if (!this.args.disabled) {
      return this.args.onCancel?.();
    }
  }

  @action
  handleEscape(event: KeyboardEvent) {
    event.stopImmediatePropagation();
    this.popover?.actions.close(event, false);
  }

  @action
  setPositionCorrectly(pos: CalculatePositionResult): CalculatePositionResult {
    const padding = 5;
    const offset =
      ((this.displayElement?.offsetHeight ?? 0) + padding) *
      (pos.verticalPosition == 'above' ? 1 : -1);

    pos.style.top = pos.style.top ?? offset;

    return pos;
  }

  @action
  async handleConfirmation(event: Event) {
    const { onSave } = this.args;

    if (!this.args.disabled && onSave) {
      this.isLoading = true;
      try {
        await onSave(event);
        this.popover?.actions.close(event, false);
      } catch (error) {
        defaultErrorHandler(error as TError);
      } finally {
        this.isLoading = false;
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvInlineEdit: typeof BvInlineEditComponent;
    'bv-inline-edit': typeof BvInlineEditComponent;
  }
}
